import { hubOfHome, redirectForTabPage } from '@/navigation/pageHubs'
import { movedHomePath, movedTo } from './movedPages'
import { Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation, type RouteObject } from 'react-router-dom'
import { ActivePageContext, PageSkeleton } from '@/design-system'
import { AppShell } from '@/layout/AppShell'
import { flattenPages, NAV_TREE, uniqueRoutes } from '@/navigation/tree'
import { pageIcon } from '@/navigation/pageIcons'
import type { NavPage } from '@/navigation/types'
import { useAuth } from '@/stores/auth'
import { hasPermission } from '@shared/permissions'
import { useClosedPages, useVisibleNav } from '@/navigation/useNavigation'
import { lazy } from 'react'
import { ForbiddenPage, IntegrationGapPage, NotFoundPage, RouteErrorBoundary, ScheduledPage } from '@/features/system/SystemPages'
import { PAGE_REGISTRY } from './pageRegistry'
const TrackingPreview = lazy(() => import('@/features/shipments/TrackingPreview.dev'))
import { usePageVisibility } from '@/stores/uiSettings'
import { EmptyState } from '@/design-system'
import { EyeSlash } from '@phosphor-icons/react'

const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const OpsAppPage = lazy(() => import('@/features/operations/OpsAppPage'))
import { OpsShell, RequireOpsAuth } from '@/features/operations/OpsShell'
import { OPS_HOME, OPS_LOGIN, OPS_PAGES } from '@/features/operations/opsApp'
import { AccountsShell, RequireAccountsAuth } from '@/features/accounts/AccountsShell'
import { ACCOUNTS_HOME, ACCOUNTS_LOGIN, onlyAccountsApp } from '@/features/accounts/accountsApp'
// متجر Aliens Materials: the one fixed public link the office hands to every client. It is deliberately outside
// RequireAuth and outside AppShell — no session, no sidebar, no redirect to /login.
const StorePage = lazy(() => import('@/features/store/StorePage'))

/** The office hid this page for everyone (or for this user) in Show / Hide settings — the link does not open it either. */
/** Sends the person to the first page his sidebar actually offers; only the home route uses it. */
function FirstOpenPage() {
  const nav = useVisibleNav()
  const first = flattenPages(nav).find(({ page }) => page.path !== '/')
  return first ? <Navigate to={first.page.path} replace /> : <HiddenPage />
}

function HiddenPage() {
  return <div className="p-8"><EmptyState icon={EyeSlash} title="هذه الصفحة مخفية من الإعدادات" description="أزيلت من القائمة للجميع أو لك من إعدادات الإظهار — تواصل مع المسؤول إذا كنت تحتاجها." /></div>
}

function RequireAuth() {
  const status = useAuth((s) => s.status)
  const granted = useAuth((s) => s.permissionSet)
  const location = useLocation()
  if (status === 'unknown') return <PageSkeleton />
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  // given only تطبيق الحسابات: the system's home opens that app, not an empty sidebar (owner, 2026-10-30)
  if (location.pathname === '/' && onlyAccountsApp(granted)) return <Navigate to={ACCOUNTS_HOME} replace />
  return <Outlet />
}

function Guarded({ page }: { page: NavPage }) {
  const location = useLocation()
  const granted = useAuth((s) => s.permissionSet)
  const visible = usePageVisibility()
  // a page closed for this user in صلاحيات المستخدمين is not reachable by its URL either
  const closed = useClosedPages()
  /*
   * A page closed for this person is not reachable by its URL either. The home page is the exception worth making:
   * landing on "this screen is hidden" every time you open the app is not a message, it is a wall — so the app opens
   * on the first page the person can actually see (owner, 2026-09-26: "لوحة التحكم ... ملوش صلاحية يختفي خالص").
   */
  // a report's home with tabs (navigation/pageHubs.ts) decides tab by tab who sees what: closing or not having its own
  // screen leaves the other tabs open
  const hub = hubOfHome(page.id)
  if (closed.has(page.id) && !hub) return page.path === '/' ? <FirstOpenPage /> : <HiddenPage />
  // a page that became a tab of a report's home: its old address opens that tab, with its query kept
  const tabUrl = redirectForTabPage(page.id, location.search)
  if (tabUrl) return <Navigate to={tabUrl} replace />
  // a screen that moved into a home page's tab (واتساب, الإشعارات والتنبيهات): its old address opens it there
  const moved = movedTo(page.id, location.search)
  const home = moved ? flattenPages().find(({ page: p }) => p.path === movedHomePath(page.id))?.page : undefined
  // …unless that home is not open to this person: then the page stays where it was
  if (moved && (!home || (!closed.has(home.id) && hasPermission(granted, home.permission) && visible(home.id)))) return <Navigate to={moved} replace />
  if (!hub && !hasPermission(granted, page.permission)) return <ForbiddenPage permission={Array.isArray(page.permission) ? page.permission.join(', ') : page.permission} />
  if (!hub && !visible(page.id)) return <HiddenPage />
  const Component = PAGE_REGISTRY[page.path]
  const section = NAV_TREE.find((g) => page.id.startsWith(`${g.id}.`))
  return (
    <ActivePageContext.Provider value={{ icon: pageIcon(page.id, section?.icon), sectionIcon: section?.icon }}>
      {Component ? <Component /> : page.status === 'gap' ? <IntegrationGapPage page={page} /> : <ScheduledPage page={page} />}
    </ActivePageContext.Provider>
  )
}

const pageRoutes: RouteObject[] = uniqueRoutes().map((page) => ({
  path: page.path === '/' ? undefined : page.path,
  index: page.path === '/' || undefined,
  element: <Guarded page={page} />,
  errorElement: <RouteErrorBoundary />,
})) as RouteObject[]

/** Detail routes that are not navigation entries. */
const extraRoutes: RouteObject[] = Object.entries(PAGE_REGISTRY)
  .filter(([path]) => !uniqueRoutes().some((p) => p.path === path))
  .map(([path, Component]) => ({ path, element: <Component />, errorElement: <RouteErrorBoundary /> }))

/**
 * The ops app's screens, inside its own frame: /ops/<the page's path>, behind the same permission checks as in the
 * main app (owner, 2026-10-08: "لوحده يفتحو بيوزر بتاعه").
 */
const opsRoutes: RouteObject[] = OPS_PAGES.map(({ path }) => {
  const page = uniqueRoutes().find((p) => p.path === path)
  const Component = PAGE_REGISTRY[path]
  return { path: path.slice(1), element: page ? <Guarded page={page} /> : Component ? <Component /> : <NotFoundPage />, errorElement: <RouteErrorBoundary /> }
})

export const router = createBrowserRouter([
  // design review on the dev server only — the chunk is never part of a production build
  ...(import.meta.env.DEV ? [{ path: '/__preview/tracking', element: <Suspense fallback={<PageSkeleton />}><TrackingPreview /></Suspense> }] : []),
  { path: '/login', element: <Suspense fallback={<PageSkeleton />}><LoginPage /></Suspense> },
  { path: OPS_LOGIN, element: <Suspense fallback={<PageSkeleton />}><LoginPage home={OPS_HOME} app="تطبيق التشغيل" /></Suspense> },
  {
    path: OPS_HOME,
    element: <RequireOpsAuth />,
    children: [{ element: <OpsShell />, children: [{ index: true, element: <OpsAppPage />, errorElement: <RouteErrorBoundary /> }, ...opsRoutes, { path: '*', element: <Navigate to={OPS_HOME} replace /> }] }],
  },
  // تطبيق الحسابات: its own sign-in and frame, one screen (features/accounts)
  { path: ACCOUNTS_LOGIN, element: <Suspense fallback={<PageSkeleton />}><LoginPage home={ACCOUNTS_HOME} app="تطبيق الحسابات" /></Suspense> },
  {
    path: ACCOUNTS_HOME,
    element: <RequireAccountsAuth />,
    children: [{ element: <AccountsShell />, errorElement: <RouteErrorBoundary />, children: [{ index: true, element: null }, { path: '*', element: <Navigate to={ACCOUNTS_HOME} replace /> }] }],
  },
  { path: '/store', element: <Suspense fallback={<PageSkeleton />}><StorePage /></Suspense>, errorElement: <RouteErrorBoundary /> },
  {
    element: <RequireAuth />,
    children: [{ element: <AppShell />, children: [...pageRoutes, ...extraRoutes, { path: '*', element: <NotFoundPage /> }] }],
  },
])
